/**
 * ResourceAccordionChart — accordion FTE chart using the same tree pattern as TimelineGantt.
 *
 * Same initiative grouping, expand/collapse, sticky HTML labels, SVG chart area —
 * but with FTE-scaled horizontal bars instead of date-scaled Gantt bars.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import type { AdjacencyNode } from '../../types/adjacency';
import type { ResourceMetrics } from '../../types/resource-config';
import { useAdjacencyStore } from '../../store/adjacency-store';
import { NodeModal } from '../editors/NodeModal';
import { INITIATIVE_COLORS } from '../../utils/map-colors';
import type { BudgetProject } from '../../utils/budget-import';
import { nodeFteSplit } from '../../utils/fte';
import budgetData from '../../data/budget-2026.json';

const budgetProjects = budgetData.projects as BudgetProject[];

// ── Constants ──────────────────────────────────────────────────────────

const ROW_HEIGHT = 30;
const LABEL_WIDTH = 360;
const MIN_CHART_WIDTH = 300;
const TOP_PAD = 32;
const BOTTOM_PAD = 8;
const RIGHT_PAD = 20;

// ── Types ──────────────────────────────────────────────────────────────

interface Props {
  nodes: AdjacencyNode[];
  allNodes: AdjacencyNode[];
  metrics: ResourceMetrics;
}

interface TreeNode {
  node: AdjacencyNode;
  children: TreeNode[];
}

type DisplayRow =
  | { kind: 'initiative'; initiative: string; nodeCount: number; isOpen: boolean }
  | {
      kind: 'node';
      node: AdjacencyNode;
      depth: number;
      hasChildren: boolean;
      isExpanded: boolean;
      budgetPriority: number;
    };

// ── Initiative group ordering ──────────────────────────────────────────

const INITIATIVE_ORDER = [
  'US Mining',
  'Proximity Detection',
  'Analytics',
  'Maritime',
  'International Mining',
  'New Verticals',
  'Capabilities',
  'Other',
];

// ── Tree builder (identical to TimelineGantt) ──────────────────────────

function buildNodeTree(
  nodes: AdjacencyNode[],
  allNodes: AdjacencyNode[],
): Map<string, TreeNode[]> {
  const nodeMap = new Map<string, AdjacencyNode>();
  for (const n of allNodes) nodeMap.set(n.id, n);

  const childrenOf = new Map<string, Set<string>>();
  for (const n of nodes) {
    for (const pid of n.parentIds) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, new Set());
      childrenOf.get(pid)!.add(n.id);
    }
    for (const edge of n.edges) {
      if (edge.relationship === 'child') {
        if (!childrenOf.has(n.id)) childrenOf.set(n.id, new Set());
        childrenOf.get(n.id)!.add(edge.targetId);
      }
      if (edge.relationship === 'parent') {
        if (!childrenOf.has(edge.targetId)) childrenOf.set(edge.targetId, new Set());
        childrenOf.get(edge.targetId)!.add(n.id);
      }
    }
  }

  const isChild = new Set<string>();
  for (const kids of childrenOf.values()) {
    for (const cid of kids) isChild.add(cid);
  }

  const filteredSet = new Set(nodes.map((n) => n.id));

  function buildSubTree(nodeId: string, visited: Set<string>): TreeNode | null {
    if (visited.has(nodeId)) return null;
    const n = nodeMap.get(nodeId);
    if (!n || !filteredSet.has(n.id)) return null;
    visited.add(nodeId);

    const kids = childrenOf.get(nodeId);
    const children: TreeNode[] = [];
    if (kids) {
      for (const cid of kids) {
        const child = buildSubTree(cid, visited);
        if (child) children.push(child);
      }
      children.sort((a, b) => a.node.name.localeCompare(b.node.name));
    }
    return { node: n, children };
  }

  const rootNodes = nodes.filter((n) => !isChild.has(n.id));
  const globalVisited = new Set<string>();
  const grouped = new Map<string, TreeNode[]>();
  for (const root of rootNodes) {
    const tree = buildSubTree(root.id, globalVisited);
    if (!tree) continue;
    const group = root.strategicInitiative || 'Other';
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group)!.push(tree);
  }

  for (const trees of grouped.values()) {
    trees.sort((a, b) => a.node.name.localeCompare(b.node.name));
  }
  return grouped;
}

// ── Flatten tree into display rows ─────────────────────────────────────

function countAll(trees: TreeNode[]): number {
  let n = 0;
  function walk(t: TreeNode) { n++; t.children.forEach(walk); }
  trees.forEach(walk);
  return n;
}

function flattenTree(
  sortedGroups: { initiative: string; trees: TreeNode[] }[],
  expandedInitiatives: Set<string>,
  expandedNodes: Set<string>,
  bpMap: Map<string, BudgetProject>,
): DisplayRow[] {
  const rows: DisplayRow[] = [];

  for (const { initiative, trees } of sortedGroups) {
    const nodeCount = countAll(trees);
    const isOpen = expandedInitiatives.has(initiative);
    rows.push({ kind: 'initiative', initiative, nodeCount, isOpen });

    if (!isOpen) continue;

    function walk(tree: TreeNode, depth: number) {
      const bp = tree.node.budgetProjectName ? bpMap.get(tree.node.budgetProjectName) : null;
      rows.push({
        kind: 'node',
        node: tree.node,
        depth,
        hasChildren: tree.children.length > 0,
        isExpanded: expandedNodes.has(tree.node.id),
        budgetPriority: bp?.budgetPriority ?? 999,
      });

      if (expandedNodes.has(tree.node.id)) {
        for (const child of tree.children) {
          walk(child, depth + 1);
        }
      }
    }

    for (const tree of trees) walk(tree, 0);
  }

  return rows;
}

// ── Status pill color helper ───────────────────────────────────────────

function statusPillColor(status: string): { bg: string; text: string } {
  switch (status) {
    case 'In Progress':
      return { bg: 'rgba(34,197,94,0.15)', text: '#86efac' };
    case 'Complete':
    case 'Completed':
      return { bg: 'rgba(59,130,246,0.15)', text: '#93c5fd' };
    case 'On Hold':
      return { bg: 'rgba(234,179,8,0.15)', text: '#fde047' };
    case 'Not Started':
      return { bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' };
    case 'Proposed/Pending':
      return { bg: 'rgba(168,85,247,0.15)', text: '#c4b5fd' };
    case 'Blocked':
      return { bg: 'rgba(239,68,68,0.15)', text: '#fca5a5' };
    default:
      return { bg: 'rgba(148,163,184,0.1)', text: '#94a3b8' };
  }
}

// ── Component ──────────────────────────────────────────────────────────

export function ResourceAccordionChart({ nodes, allNodes, metrics }: Props) {
  const {
    resourceConfig,
    updateNode, deleteNode, duplicateNode,
    createParentChildLink, createRelatedLink, removeRelationship, convertEdgeType,
  } = useAdjacencyStore();
  const [expandedInitiatives, setExpandedInitiatives] = useState<Set<string>>(new Set());
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [hover, setHover] = useState<{ nodeId: string; mx: number } | null>(null);
  const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(MIN_CHART_WIDTH);

  // Measure the chart area dynamically
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width) - RIGHT_PAD;
        setChartWidth(Math.max(MIN_CHART_WIDTH, w));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Budget priority map
  const bpMap = useMemo(() => {
    const m = new Map<string, BudgetProject>();
    for (const bp of budgetProjects) m.set(bp.project, bp);
    return m;
  }, []);

  // Build tree
  const groupedTree = useMemo(() => buildNodeTree(nodes, allNodes), [nodes, allNodes]);
  const sortedGroups = useMemo(
    () =>
      INITIATIVE_ORDER.filter((g) => groupedTree.has(g)).map((g) => ({
        initiative: g,
        trees: groupedTree.get(g)!,
      })),
    [groupedTree],
  );

  // Flatten to display rows
  const displayRows = useMemo(
    () => flattenTree(sortedGroups, expandedInitiatives, expandedNodes, bpMap),
    [sortedGroups, expandedInitiatives, expandedNodes, bpMap],
  );

  // Toggle helpers
  const toggleInitiative = useCallback((initiative: string) => {
    setExpandedInitiatives((prev) => {
      const next = new Set(prev);
      if (next.has(initiative)) next.delete(initiative);
      else next.add(initiative);
      return next;
    });
  }, []);

  const toggleNode = useCallback((id: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const allInits = new Set(sortedGroups.map((g) => g.initiative));
    setExpandedInitiatives(allInits);
    const allIds = new Set<string>();
    function collect(tree: TreeNode) {
      if (tree.children.length > 0) allIds.add(tree.node.id);
      tree.children.forEach(collect);
    }
    for (const group of sortedGroups) group.trees.forEach(collect);
    setExpandedNodes(allIds);
  }, [sortedGroups]);

  const collapseAll = useCallback(() => {
    setExpandedInitiatives(new Set());
    setExpandedNodes(new Set());
  }, []);

  // ── FTE data computation ──────────────────────────────────────────────

  // WQ-343: per-node FTE is nodeFte (same as the List/Finance table and the
  // Finance chart); swim-lane FTE stays in the totals only.
  const fteData = useMemo(() => {
    const map = new Map<string, { iwt: number; iwtSouth: number; total: number }>();
    for (const n of nodes) {
      const totalInv = (n.iRnD ?? 0) + (n.fRnD ?? 0);
      if (totalInv === 0) continue;
      const bp = n.budgetProjectName ? bpMap.get(n.budgetProjectName) : null;
      map.set(n.id, nodeFteSplit(n, resourceConfig, bp?.iwtSouthFte ?? 0));
    }
    return map;
  }, [nodes, resourceConfig, bpMap]);

  // Count nodes with FTE data for header
  const budgetNodeCount = fteData.size;

  // ── FTE scale ──────────────────────────────────────────────────────────

  const maxFte = useMemo(() => {
    let max = 0;
    for (const d of fteData.values()) {
      if (d.total > max) max = d.total;
    }
    return Math.max(2, Math.ceil(max));
  }, [fteData]);

  const toX = (fte: number) => (fte / maxFte) * chartWidth;

  const step = maxFte > 10 ? 2 : 1;
  const ticks = Array.from({ length: Math.floor(maxFte / step) + 1 }, (_, i) => i * step);

  const svgHeight = TOP_PAD + displayRows.length * ROW_HEIGHT + BOTTOM_PAD;

  return (
    <div className="flex flex-col h-full">
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b shrink-0"
        style={{ borderColor: 'var(--glass-border)' }}
      >
        <h3 className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
          FTE Allocation ({budgetNodeCount} projects)
        </h3>
        <div className="flex items-center gap-4">
          {/* Legend */}
          <div className="flex gap-3 text-[10px]" style={{ color: 'var(--text-dim)' }}>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#3b82f6' }} />
              IWT
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#14b8a6' }} />
              IWT South
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-px h-2.5 border-l border-dashed" style={{ borderColor: '#f59e0b' }} />
              Avg Capacity
            </span>
          </div>
          <div className="flex gap-2">
            <button onClick={expandAll} className="text-[10px] hover:underline"
              style={{ color: 'var(--text-dim)' }}>Expand All</button>
            <button onClick={collapseAll} className="text-[10px] hover:underline"
              style={{ color: 'var(--text-dim)' }}>Collapse All</button>
          </div>
        </div>
      </div>

      {/* Scrollable area */}
      <div className="flex-1 overflow-auto min-h-0" ref={scrollRef}>
        <div
          style={{
            display: 'flex',
            minWidth: LABEL_WIDTH + MIN_CHART_WIDTH + RIGHT_PAD,
          }}
        >
          {/* Left: HTML label column — sticky */}
          <div
            style={{
              width: LABEL_WIDTH,
              flexShrink: 0,
              position: 'sticky',
              left: 0,
              zIndex: 2,
              background: 'var(--canvas-bg, #0f172a)',
              borderRight: '1px solid var(--glass-border)',
            }}
          >
            {/* Column headers */}
            <div
              style={{ height: TOP_PAD, borderColor: 'var(--glass-border)' }}
              className="flex items-end pb-1 border-b text-[11px]"
            >
              <span style={{ width: 30, flexShrink: 0, paddingLeft: 8, color: 'var(--text-muted)', fontWeight: 500 }}>#</span>
              <span className="flex-1 truncate" style={{ paddingLeft: 4, color: 'var(--text-muted)', fontWeight: 500 }}>Project</span>
              <span style={{ width: 75, flexShrink: 0, textAlign: 'right', paddingRight: 4, color: 'var(--text-muted)', fontWeight: 500 }}>Phase</span>
              <span style={{ width: 80, flexShrink: 0, textAlign: 'right', paddingRight: 8, color: 'var(--text-muted)', fontWeight: 500 }}>Status</span>
            </div>

            {/* Rows */}
            {displayRows.map((row) => {
              if (row.kind === 'initiative') {
                return (
                  <div
                    key={`init-${row.initiative}`}
                    style={{ height: ROW_HEIGHT }}
                    className="flex items-center gap-2 px-2 cursor-pointer"
                    onClick={() => toggleInitiative(row.initiative)}
                  >
                    <ChevronRight
                      size={12}
                      className="shrink-0"
                      style={{
                        transform: row.isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        color: 'var(--text-dim)',
                        transition: 'transform 150ms',
                      }}
                    />
                    <span
                      className="text-[11px] font-semibold"
                      style={{
                        color:
                          INITIATIVE_COLORS[
                            row.initiative as keyof typeof INITIATIVE_COLORS
                          ] ?? 'var(--text-muted)',
                      }}
                    >
                      {row.initiative}
                    </span>
                    <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>
                      ({row.nodeCount})
                    </span>
                  </div>
                );
              }

              // Node row
              const isHovered = hover?.nodeId === row.node.id;
              const hasFteData = fteData.has(row.node.id);

              return (
                <div
                  key={row.node.id}
                  style={{
                    height: ROW_HEIGHT,
                    background: isHovered ? 'rgba(59,130,246,0.08)' : 'transparent',
                  }}
                  className="flex items-center cursor-pointer transition-colors"
                  onMouseEnter={() => setHover({ nodeId: row.node.id, mx: 0 })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => row.hasChildren && toggleNode(row.node.id)}
                  onDoubleClick={() => setDetailNodeId(row.node.id)}
                >
                  {/* # column */}
                  <span
                    className="text-[11px] tabular-nums"
                    style={{ width: 30, flexShrink: 0, paddingLeft: 8, color: 'var(--text-muted)', fontWeight: 500 }}
                  >
                    {row.budgetPriority < 999 ? row.budgetPriority : ''}
                  </span>

                  {/* Project column */}
                  <div
                    className="flex-1 flex items-center gap-1.5 min-w-0"
                    style={{ paddingLeft: `${row.depth * 18}px` }}
                  >
                    {row.hasChildren ? (
                      <ChevronRight
                        size={12}
                        className="shrink-0"
                        style={{
                          transform: row.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          color: 'var(--text-dim)',
                          transition: 'transform 150ms',
                        }}
                      />
                    ) : (
                      <span style={{ width: 12 }} className="shrink-0" />
                    )}

                    {/* FTE dot */}
                    <span
                      className="shrink-0 rounded-full"
                      style={{
                        width: 5,
                        height: 5,
                        background: hasFteData ? '#3b82f6' : 'rgba(255,255,255,0.12)',
                      }}
                    />

                    {/* Node name */}
                    <span
                      className="text-[11px] truncate"
                      style={{
                        color: isHovered ? 'var(--text-primary)' : 'var(--text-secondary)',
                      }}
                    >
                      {row.node.name}
                    </span>
                  </div>

                  {/* Phase column */}
                  <span style={{ width: 75, flexShrink: 0 }} className="flex justify-end pr-1">
                    {row.node.phase && (
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(99,102,241,0.15)', color: '#a5b4fc' }}
                      >
                        {row.node.phase}
                      </span>
                    )}
                  </span>

                  {/* Status column */}
                  <span style={{ width: 80, flexShrink: 0 }} className="flex justify-end pr-2">
                    {row.node.status && (
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full"
                        style={{
                          background: statusPillColor(row.node.status).bg,
                          color: statusPillColor(row.node.status).text,
                        }}
                      >
                        {row.node.status}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Right: SVG chart area — fills remaining width */}
          <div ref={chartRef} style={{ flex: 1, minWidth: MIN_CHART_WIDTH + RIGHT_PAD }}>
          <svg
            width={chartWidth + RIGHT_PAD}
            height={svgHeight}
            className="block"
          >
            {/* FTE scale gridlines + labels */}
            {ticks.map((fte) => {
              const x = toX(fte);
              return (
                <g key={fte}>
                  <line x1={x} y1={TOP_PAD - 4} x2={x} y2={svgHeight}
                    stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
                  <text x={x} y={TOP_PAD - 10} fill="var(--text-muted)"
                    fontSize={11} fontWeight={500} textAnchor="middle">
                    {fte}
                  </text>
                </g>
              );
            })}

            {/* Average FTE per project — dashed threshold line */}
            {(() => {
              const projectCount = fteData.size;
              if (projectCount === 0) return null;
              const avgFte = metrics.totalAvailableFtes / projectCount;
              if (avgFte > maxFte) return null;
              const ax = toX(avgFte);
              return (
                <>
                  <line x1={ax} y1={TOP_PAD} x2={ax} y2={svgHeight}
                    stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.6} />
                  <text x={ax + 3} y={TOP_PAD - 10}
                    fill="#f59e0b" fontSize={9} fontWeight={600}>
                    Avg ({avgFte.toFixed(1)})
                  </text>
                </>
              );
            })()}

            {/* Rows */}
            {displayRows.map((row, i) => {
              const y = TOP_PAD + i * ROW_HEIGHT;

              // Initiative header row: subtle separator line
              if (row.kind === 'initiative') {
                return (
                  <line
                    key={`sep-${row.initiative}`}
                    x1={0}
                    y1={y + ROW_HEIGHT - 0.5}
                    x2={chartWidth + RIGHT_PAD}
                    y2={y + ROW_HEIGHT - 0.5}
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth={1}
                  />
                );
              }

              // Node row — only render bar if it has FTE data
              const n = row.node;
              const d = fteData.get(n.id);
              if (!d || d.total === 0) return null;

              const barY = y + 7;
              const barH = ROW_HEIGHT - 14;
              const isHovered = hover?.nodeId === n.id;

              // IWT bar (blue)
              const iwtWidth = Math.max(0, toX(d.iwt));
              // IWT South bar (teal) — stacked after IWT
              const southWidth = Math.max(0, toX(d.iwtSouth));

              return (
                <g
                  key={n.id}
                  onMouseEnter={(e) => {
                    const svg = e.currentTarget.closest('svg');
                    if (!svg) return;
                    const pt = svg.createSVGPoint();
                    pt.x = e.clientX;
                    pt.y = e.clientY;
                    const svgPt = pt.matrixTransform(svg.getScreenCTM()!.inverse());
                    setHover({ nodeId: n.id, mx: svgPt.x });
                  }}
                  onMouseMove={(e) => {
                    const svg = e.currentTarget.closest('svg');
                    if (!svg) return;
                    const pt = svg.createSVGPoint();
                    pt.x = e.clientX;
                    pt.y = e.clientY;
                    const svgPt = pt.matrixTransform(svg.getScreenCTM()!.inverse());
                    setHover({ nodeId: n.id, mx: svgPt.x });
                  }}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Row hover background */}
                  {isHovered && (
                    <rect x={0} y={y} width={chartWidth + RIGHT_PAD} height={ROW_HEIGHT}
                      fill="rgba(255,255,255,0.03)" />
                  )}

                  {/* IWT FTE bar */}
                  {iwtWidth > 0 && (
                    <rect x={0} y={barY} width={iwtWidth} height={barH} rx={3}
                      fill="#3b82f6" fillOpacity={isHovered ? 0.9 : 0.6}
                      stroke={isHovered ? '#3b82f6' : 'none'} strokeWidth={isHovered ? 1.5 : 0} />
                  )}

                  {/* IWT South FTE bar (stacked) */}
                  {southWidth > 0 && (
                    <rect x={iwtWidth} y={barY} width={southWidth} height={barH}
                      rx={iwtWidth === 0 ? 3 : 0}
                      fill="#14b8a6" fillOpacity={isHovered ? 0.9 : 0.6}
                      stroke={isHovered ? '#14b8a6' : 'none'} strokeWidth={isHovered ? 1.5 : 0} />
                  )}

                  {/* FTE label at end of bar */}
                  {d.total > 0 && (
                    <text x={iwtWidth + southWidth + 6} y={y + ROW_HEIGHT / 2 + 3.5}
                      fill={isHovered ? 'var(--text-primary)' : 'var(--text-dim)'}
                      fontSize={10} fontWeight={isHovered ? 600 : 400}>
                      {d.total.toFixed(1)}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Tooltip layer — renders LAST so it's always on top */}
            {hover && (() => {
              const rowIndex = displayRows.findIndex(
                (r) => r.kind === 'node' && r.node.id === hover.nodeId
              );
              if (rowIndex === -1) return null;
              const row = displayRows[rowIndex];
              if (row.kind !== 'node') return null;
              const n = row.node;
              const d = fteData.get(n.id);
              if (!d) return null;

              const y = TOP_PAD + rowIndex * ROW_HEIGHT;
              const tx = Math.max(0, Math.min(hover.mx - 100, chartWidth - 200));
              const pctOfDemand = metrics.ftesNeeded > 0
                ? ((d.total / metrics.ftesNeeded) * 100).toFixed(1)
                : '0';
              const lines = [
                n.name,
                `IWT: ${d.iwt.toFixed(2)} FTE`,
                ...(d.iwtSouth > 0 ? [`IWT South: ${d.iwtSouth.toFixed(2)} FTE`] : []),
                `Total: ${d.total.toFixed(2)} FTE`,
                `${pctOfDemand}% of demand`,
              ];
              const tipH = lines.length * 14 + 12;

              const belowY = y + ROW_HEIGHT + 2;
              const aboveY = Math.max(0, y - tipH - 2);
              const ty = (belowY + tipH > svgHeight) ? aboveY : belowY;

              return (
                <g style={{ pointerEvents: 'none' }}>
                  <rect x={tx} y={ty} width={200} height={tipH} rx={6}
                    fill="#0f172a" stroke="rgba(255,255,255,0.2)" strokeWidth={1} />
                  {lines.map((line, li) => (
                    <text key={li} x={tx + 8} y={ty + 14 + li * 14}
                      fill={li === 0 ? '#f1f5f9' : '#94a3b8'}
                      fontSize={li === 0 ? 11 : 10}
                      fontWeight={li === 0 ? 600 : 400}>
                      {line}
                    </text>
                  ))}
                </g>
              );
            })()}
          </svg>
          </div>
        </div>
      </div>

      {/* NodeModal — double-click to edit */}
      {detailNodeId && (() => {
        const detailNode = allNodes.find((n) => n.id === detailNodeId);
        if (!detailNode) return null;
        return (
          <NodeModal node={detailNode} allNodes={allNodes}
            onClose={() => setDetailNodeId(null)}
            onSave={(id, changes) => { void updateNode(id, changes); }}
            onDelete={(id) => { void deleteNode(id); setDetailNodeId(null); }}
            onDuplicate={(id) => { void duplicateNode(id); }}
            onAddParent={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
            onAddChild={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
            onAddRelated={(a, b) => { void createRelatedLink(a, b); }}
            onRemoveRelationship={(a, b) => { void removeRelationship(a, b); }}
            onConvertEdgeType={(a, b, toType, parentId) => { void convertEdgeType(a, b, toType, parentId); }}
          />
        );
      })()}
    </div>
  );
}
